<template>
    <div>
        <lti-drawer
            :active="showEbookPreviewDrawer"
            :title="`Ebook: ${selectedEbookTitle}`"
            is-ebook
            :maximized="isDrawerMaximized"
            @close="$emit('close')"
            @update:active="$emit('update:active', $event)"
            @restore="handleRestore"
            @state="handleMaximizedChange"
        >
            <div class="ebook-drawer-body is-flex is-flex-direction-column is-height-full">
                <!-- Loading VitalSource Info -->
                <div v-if="vitalSourceInfoLoading" class="ebook-loading-overlay">
                    <div class="has-text-centered p-6">
                        <e-icon icon="spinner" spin />
                        <p class="mt-2">Loading integration info…</p>
                    </div>
                </div>

                <!-- VitalSource Info Load Failure -->
                <div v-else-if="vitalSourceInfoError" class="ebook-loading-overlay">
                    <div class="has-text-centered p-6">
                        <h3 class="title is-4 mb-4">Could not load Ebook integration info</h3>
                        <p class="mt-2">Please check your connection and try again.</p>
                        <button class="button is-primary mt-5" @click="$emit('retry-vital-source-info')">
                            Try again
                        </button>
                    </div>
                </div>

                <!-- Ebook Iframe Container -->
                <div v-else-if="canAccessEbookSelector" ref="ltiContainer" class="iframe-container is-flex-grow-1">
                    <!-- LTI Auth Failure Message -->
                    <div v-if="ltiAuthFailed">
                        <div class="ebook-auth-failure p-6">
                            <h3 class="title is-4 mb-4">Browser settings blocked the Ebook reader</h3>
                            <p>
                                Some browser privacy settings (like preventing cross-site tracking or cookies) are
                                blocking the Ebook reader in Edverum.
                            </p>
                            <p>For the best experience viewing Ebooks, please update your settings.</p>
                            <p>
                                Alternatively you can open the book in this tab (replacing Edverum) by clicking on the
                                button below.
                            </p>
                            <button class="button is-primary mt-5" @click="handleContinueInTab">
                                Open Ebook in this tab
                            </button>
                        </div>
                    </div>

                    <div v-else-if="loading" class="ebook-loading-overlay">
                        <div class="has-text-centered p-6">
                            <e-icon icon="spinner" spin />
                            <p class="mt-2">Loading Ebook…</p>
                        </div>
                    </div>
                </div>

                <!-- Access Denied Message -->
                <div v-else class="ebook-access-denied-overlay">
                    <div class="has-text-centered p-6">
                        <h3 class="title is-4 mb-4">Ebook Feature Unavailable</h3>
                        <p v-if="!isVitalSourceIntegrated" class="mt-4">
                            The Edverum - VitalSource Integration is currently disabled.<br />
                            <a href="#" class="support-link" @click.prevent="redirectToSupport">
                                Please contact support if you believe you see this message in error.
                            </a>
                        </p>
                        <p v-else class="mt-4">
                            You do not have permission to access eBooks.<br />
                            <a href="#" class="support-link" @click.prevent="redirectToSupport">
                                Please contact support if you believe you see this message in error.
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </lti-drawer>
    </div>
</template>

<script>
import queryString from 'query-string';
import LtiDrawer from '@/components/lti/LtiDrawer.vue';
import UserSetting from '@/models/UserSetting.js';

export default {
    name: 'EbookPreviewDrawer',

    components: {
        LtiDrawer,
    },

    props: {
        ltiResourceLinkId: {
            type: [String, Number],
            required: true,
        },
        selectedEbookTitle: {
            type: String,
            default: '',
        },
        selectedEbookFormat: {
            type: String,
            default: '',
        },
        showEbookPreviewDrawer: {
            type: Boolean,
            default: false,
        },
        hasSpecificLocation: {
            type: Boolean,
            default: false,
        },
        continueReading: {
            type: Boolean,
            default: false,
        },
        vitalSourceInfoLoaded: {
            type: Boolean,
            default: false,
        },
        vitalSourceInfoError: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['close', 'update:active', 'retry-vital-source-info'],

    data() {
        return {
            loading: false,
            iframeEl: null,
            iframeLoadHandler: null,
            isDrawerMaximized: false,
            ltiAuthFailed: false,
        };
    },

    computed: {
        ltiParameters() {
            const params = {
                has_specific_location: this.hasSpecificLocation,
                continue_reading: this.continueReading,
            };

            if (this.selectedEbookFormat) {
                params.format = this.selectedEbookFormat;
            }

            return queryString.stringify(params);
        },

        isVitalSourceIntegrated() {
            return this.$store.state.Integrations.isVitalSourceInIntegrationEnabled;
        },

        currentUser() {
            return this.$store.state.User;
        },

        hasRequiredPermissions() {
            // Check if user has admin or instructor role
            if (!this.currentUser) {
                return false;
            }

            return (
                this.$store.getters['User/hasRole']('Admin') ||
                this.$store.getters['User/hasRole']('Owner') ||
                this.$store.getters['User/hasRole']('Instructor') ||
                this.$store.getters['User/hasRole']('Instruction Admin') ||
                this.$store.getters['User/hasRole']('Student')
            );
        },

        canAccessEbookSelector() {
            return this.isVitalSourceIntegrated && this.hasRequiredPermissions;
        },

        vitalSourceInfoLoading() {
            return !this.vitalSourceInfoLoaded && !this.vitalSourceInfoError;
        },
    },

    watch: {
        showEbookPreviewDrawer(isActive) {
            if (isActive) {
                this.loadSavedDrawerState();
                // Only load if VitalSource info is already loaded
                if (this.vitalSourceInfoLoaded && this.canAccessEbookSelector) {
                    this.loadLaunchData();
                }
            } else {
                this.cleanup();
            }
        },

        ltiResourceLinkId(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                // MUST reload iframe for LTI to pick up accept_multiple
                this.loadLaunchData();
            }
        },

        hasSpecificLocation(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },

        continueReading(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },

        selectedEbookFormat(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },

        canAccessEbookSelector(canAccess, previousValue) {
            // If access is revoked while open, tear down iframe state immediately.
            if (!canAccess) {
                this.cleanup();
                return;
            }

            // When access is granted after initially being denied, schedule load.
            if (canAccess && !previousValue && this.showEbookPreviewDrawer) {
                this.$nextTick(() => {
                    if (!this.showEbookPreviewDrawer || !this.vitalSourceInfoLoaded || !this.canAccessEbookSelector) {
                        return;
                    }

                    this.loadLaunchData();
                });
            }
        },

        vitalSourceInfoLoaded(isLoaded) {
            // If readiness is revoked while open, tear down iframe state immediately.
            if (!isLoaded) {
                this.cleanup();
                return;
            }

            // When VitalSource info finishes loading and drawer is active, schedule load.
            if (isLoaded && this.showEbookPreviewDrawer && this.canAccessEbookSelector) {
                this.$nextTick(() => {
                    if (!this.showEbookPreviewDrawer || !this.vitalSourceInfoLoaded || !this.canAccessEbookSelector) {
                        return;
                    }

                    this.loadLaunchData();
                });
            }
        },
    },

    mounted() {
        window.addEventListener('message', this.handleLtiLaunchData);

        if (this.showEbookPreviewDrawer) {
            this.loadSavedDrawerState();
            // Only load if VitalSource info is already loaded
            if (this.vitalSourceInfoLoaded && this.canAccessEbookSelector) {
                this.loadLaunchData();
            }
        }
    },

    beforeUnmount() {
        window.removeEventListener('message', this.handleLtiLaunchData);
        this.cleanup();
    },

    methods: {
        async handleLtiLaunchData(event) {
            const { type } = event.data || {};

            if (!type) return;

            if (type === 'LTI_AUTH_FAILED') {
                this.handleLtiAuthFailure();
            }
        },

        handleLtiAuthFailure() {
            // stop iframe loading state
            this.loading = false;

            // remove iframe completely (important)
            if (this.iframeEl?.parentNode) {
                this.iframeEl.parentNode.removeChild(this.iframeEl);
            }

            // Show auth failure message in drawer
            this.ltiAuthFailed = true;
        },

        handleContinueInTab() {
            this.openLtiInNewTab();
            this.$emit('close');
        },

        openLtiInNewTab() {
            const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

            const url = `${baseUrl}/lti/links/${this.ltiResourceLinkId}?${this.ltiParameters}`;

            window.location.assign(url);
        },

        handleRestore(payload) {
            if (payload?.relaunch === false) {
                return;
            }

            this.loadLaunchData();
        },

        async loadSavedDrawerState() {
            try {
                const setting = UserSetting.findByKey('ebook_drawer_state');
                const savedState = setting?.value;

                if (savedState === 'fullscreen') {
                    this.isDrawerMaximized = true;
                } else if (savedState === 'normal') {
                    this.isDrawerMaximized = false;
                } else {
                    this.isDrawerMaximized = true;
                }
            } catch (error) {
                // On error, default to fullscreen mode
                this.$edverumToast.open({
                    message: 'Failed to load ebook drawer state',
                    type: 'is-warning',
                });
                this.isDrawerMaximized = true;
            }
        },

        async handleMaximizedChange(isMaximized) {
            this.isDrawerMaximized = isMaximized;
            await this.saveDrawerState(isMaximized);
        },

        async saveDrawerState(isMaximized) {
            try {
                const setting = UserSetting.findByKey('ebook_drawer_state');
                const state = isMaximized ? 'fullscreen' : 'normal';
                await setting.set(state);
            } catch (error) {
                // Silently fail - don't disrupt user experience
                this.$edverumToast.open({
                    message: 'Failed to save ebook drawer state',
                    type: 'is-warning',
                });
            }
        },

        cleanup() {
            if (this.iframeEl && this.iframeLoadHandler) {
                this.iframeEl.removeEventListener('load', this.iframeLoadHandler);
                this.iframeLoadHandler = null;
            }
            this.iframeEl = null;
            this.loading = false;
            this.ltiAuthFailed = false;
        },

        redirectToSupport() {
            this.$emit('close');
            this.$router.push({ name: 'help-home' });
        },

        async loadLaunchData() {
            if (this.ltiResourceLinkId) {
                this.$nextTick(() => {
                    const container = this.$refs.ltiContainer;
                    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

                    if (container) {
                        const src = `${baseUrl}/lti/links/${this.ltiResourceLinkId}?${this.ltiParameters}`;

                        // If a previous iframe exists, remove its load listener and clear references
                        if (this.iframeEl && this.iframeLoadHandler) {
                            this.iframeEl.removeEventListener('load', this.iframeLoadHandler);
                            this.iframeLoadHandler = null;
                        }
                        this.iframeEl = null;

                        // Recreate iframe to ensure fresh launch
                        container.innerHTML = '';

                        const iframe = document.createElement('iframe');
                        iframe.style.width = '100%';
                        iframe.style.height = '100%';
                        iframe.style.border = 'none';
                        iframe.src = src;

                        // show loading overlay until iframe loads
                        this.loading = true;

                        this.iframeLoadHandler = () => {
                            this.loading = false;
                        };

                        iframe.addEventListener('load', this.iframeLoadHandler);

                        container.appendChild(iframe);

                        this.iframeEl = iframe;
                    }
                });
            }
        },
    },
};
</script>

<style>
.iframe-container {
    position: relative;
}

/* Position the drawer body so absolutely positioned overlays
   target the drawer content rather than the viewport or other
   ancestors. */
.ebook-drawer-body {
    position: relative;
    min-height: 0;
}

/* Unified overlay rules: cover the drawer body and stay centered */
.ebook-loading-overlay,
.ebook-processing-overlay,
.ebook-access-denied-overlay {
    /* Make overlays cover the drawer content (not the whole viewport)
         so messages stay centered when the drawer content scrolls. */
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    box-sizing: border-box;
    text-align: center;
    background: rgba(255, 255, 255, 0.85);
}

/* Slightly stronger background for processing so it feels modal */
.ebook-processing-overlay {
    background: rgba(255, 255, 255, 0.94);
}

/* Dark mode backgrounds */
.app-dark .ebook-loading-overlay,
.app-dark .ebook-processing-overlay,
.app-dark .ebook-access-denied-overlay {
    background: rgba(0, 0, 0, 0.85);
    color: white;
}

/* Constrain inner content so text is readable on wide screens and scales on mobile */
.ebook-loading-overlay > .has-text-centered,
.ebook-processing-overlay > .has-text-centered,
.ebook-access-denied-overlay > .has-text-centered {
    width: 100%;
    max-width: 640px;
    padding: 1rem 1.5rem;
}

/* Responsive typography */
.ebook-loading-overlay h3,
.ebook-access-denied-overlay h3 {
    margin-bottom: 0.75rem;
    font-size: clamp(1.05rem, 2.5vw, 1.25rem);
}

.ebook-loading-overlay p,
.ebook-processing-overlay p,
.ebook-access-denied-overlay p {
    margin: 0.25rem 0 0;
    font-size: clamp(0.9rem, 2.2vw, 1rem);
    line-height: 1.4;
}

.ebook-auth-failure {
    max-width: 920px;
    text-align: left;
}

.ebook-auth-failure p {
    margin-bottom: 1.25rem;
}

.ebook-auth-failure ul {
    margin: 0 0 1.25rem 2rem;
}

/* Support link hover states */
.support-link {
    color: #3273dc;
    text-decoration: underline;
    cursor: pointer;
}

.support-link:hover {
    color: #235ba8;
}

.app-dark .support-link {
    color: #5c9aec;
}

.app-dark .support-link:hover {
    color: #7fb3f5;
}
</style>
